import {
  INSUNITS_TO_METERS,
  isAnonymousBlockName as isAnonymousBlockNameCore,
  blockInsertEntitySchema,
  type BlockDefinition,
  type DimensionStyle,
} from "@dxf-ai-editor/drawing-model";
import { getBlockInsertBoundsOrNull } from "@dxf-ai-editor/cad-core";
import type { EntityPreview } from "@dxf-ai-editor/contracts";
import {
  computeRenderedPreviewBounds,
  specializeBlockInstance,
} from "../scene/blockInstanceResolve";

/**
 * Pure helpers split out of InsertBlockDialog.tsx so the main dialog
 * file stays under 500 LOC and react-refresh's component-only export
 * rule is not violated. The reusable `InsertThreeAxisRow` JSX
 * subcomponent lives separately in `InsertThreeAxisRow.tsx`.
 *
 * - `isAnonymousBlockName` — re-export of the canonical AutoCAD
 *   anonymous-block predicate from `@dxf-ai-editor/drawing-model`.
 *   Matches `*Model_Space`, `*Paper_Space<N>`, `*D\d+`, `*U\d+`,
 *   `*X\d+`, `*E\d+`.
 * - `computeBlockBounds` — the FULL-fidelity real bounding box of a
 *   block DEFINITION's child geometry, used by `useOverlapWarningDialog`
 *   for the P7 insertion-overlap footprint. Resolved via
 *   `@dxf-ai-editor/cad-core`'s `getBlockInsertBoundsOrNull` (a synthetic
 *   identity INSERT through the SAME block-transform-chain resolver a real
 *   placed INSERT uses) so every child kind the typed model supports
 *   (incl. Ellipse / Spline / Hatch / Dimension / Multileader / Revision
 *   Cloud / nested Block-MINSERT) contributes to the overlap footprint.
 *   The block tree is first PER-INSTANCE specialized
 *   (`blockInstanceResolve.ts::specializeBlockInstance`) so nested dynamic
 *   INSERT children resolve with their OWN visibility / parameter state —
 *   exactly what the render path evaluates before drawing (codex PR #472
 *   round-3 findings 3 + 5). Correctness has no downside here — there is no
 *   rendering step to under-represent, only a yes/no overlap test.
 * - `computeBlockPreviewBounds` — the SVG preview PANE'S viewBox sizing
 *   (`InsertBlockDialog`'s "Образец" pane). Deliberately narrower than
 *   `computeBlockBounds`: it frames EXACTLY what the SVG preview renderer
 *   (`scene/blockRender.tsx::renderBlockChildEntity`) draws, computed by
 *   walking the SAME recursion the renderer walks
 *   (`blockInstanceResolve.ts::computeRenderedPreviewBounds`) rather than a
 *   hand-rolled copy — so the frame always matches the visible shape
 *   (codex PR #472 round-3 findings 1 + 4).
 * - `buildPreviewEntity` — synthesize an EntityPreview at (0,0) with
 *   identity transform so the preview pane reuses the real
 *   `renderBlockInstance` renderer.
 */

export function isAnonymousBlockName(name: string): boolean {
  return isAnonymousBlockNameCore(name);
}

/**
 * INSUNITS dropdown option values shared by the Block Definition (BLOCK)
 * and Write Block (WBLOCK) dialogs — the full DXF $INSUNITS /
 * BLOCK_RECORD group 70 enumeration, 0 (Unitless) .. 21 (US Survey Feet,
 * added in AutoCAD 2017). Derived from the canonical drawing-model unit
 * table so a code added there automatically appears in every picker
 * (single source of truth; Codex#248 "survey-foot pickers" — the
 * per-dialog literal lists previously stopped at 20).
 */
export const INSUNITS_VALUES: readonly number[] = Object.keys(
  INSUNITS_TO_METERS,
)
  .map(Number)
  .sort((a, b) => a - b);

const DEFAULT_BLOCK_PREVIEW_BOUNDS = { minX: -10, minY: -10, maxX: 10, maxY: 10 };

/** Pad a resolved AABB by 8% of its larger extent (minimum 1 unit) — one
 * truth shared by {@link computeBlockBounds} and
 * {@link computeBlockPreviewBounds} so the framing margin never drifts
 * between the overlap-warning footprint and the preview-pane viewBox. */
function padBounds(bounds: {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}): { minX: number; minY: number; maxX: number; maxY: number } {
  const pad = Math.max(
    (bounds.maxX - bounds.minX) * 0.08,
    (bounds.maxY - bounds.minY) * 0.08,
    1,
  );
  return {
    minX: bounds.minX - pad,
    minY: bounds.minY - pad,
    maxX: bounds.maxX + pad,
    maxY: bounds.maxY + pad,
  };
}

/**
 * Real bounding box of a block DEFINITION's child geometry, in the
 * block's own LOCAL (authoring-origin) frame — the P7 overlap-warning
 * footprint.
 *
 * The block tree is PER-INSTANCE specialized first
 * (`specializeBlockInstance`): every nested INSERT reference is rewritten to
 * a uniquely-named synthetic definition, pre-evaluated with THAT instance's
 * own dynamic state (visibility / parameters), so a static outer block whose
 * nested dynamic child hides far-off geometry no longer includes that hidden
 * geometry in the footprint, and two nested INSERTs of the SAME dynamic block
 * with DIFFERENT state each contribute their own geometry (codex PR #472
 * round-3 findings 2 + 5). Unresolved (missing / cyclic) nested references
 * become insertion-point markers (finding 3). The specialized, self-contained
 * table is then resolved by cad-core's `getBlockInsertBoundsOrNull` (a
 * synthetic identity INSERT — for an identity transform the resolved WORLD
 * bounds equal the definition's own LOCAL bounds exactly), which returns
 * `null` (→ the fabricated default box) ONLY when the block genuinely has no
 * usable geometry — distinguishing it from a legitimate degenerate `{0,0,0,0}`
 * AABB (a lone Point/Text/Attdef child at the block's local origin).
 *
 * `blockDefinitions` (optional) is the FULL document block-definition table
 * (`DrawingDocument.blocks`) so nested Block children resolve their own
 * referenced definitions too; `blockDef` itself is always merged in under its
 * own name (defensive — a caller need not pre-include it).
 *
 * `dimStylesByName` (optional, `DrawingDocument.styleTables.dimensionStyles`
 * keyed by name) resolves every DIMENSION descendant's referenced DIMSTYLE onto
 * its `styleOverrides` while the tree is specialized, so a dimension INSIDE the
 * inserted block frames the same style-driven extension / text padding cad-core
 * would otherwise miss (codex PR #472 round-5 finding 1).
 */
export function computeBlockBounds(
  blockDef: BlockDefinition | undefined,
  blockDefinitions?: Readonly<Record<string, BlockDefinition>>,
  dimStylesByName?: ReadonlyMap<string, DimensionStyle>,
): { minX: number; minY: number; maxX: number; maxY: number } {
  if (!blockDef || blockDef.entities.length === 0) {
    return DEFAULT_BLOCK_PREVIEW_BOUNDS;
  }

  const fullTable: Record<string, BlockDefinition> = {
    ...blockDefinitions,
    [blockDef.name]: blockDef,
  };
  // Per-instance specialization (findings 2 + 3 + 5) — evaluate nested
  // dynamic INSERT children with their OWN state before cad-core resolves the
  // (name-keyed) geometry; `dimStylesByName` resolves nested DIMENSION children
  // against the DIMSTYLE table in the same pass (round-5 finding 1).
  const spec = specializeBlockInstance(blockDef.name, {}, fullTable, dimStylesByName);
  if (!spec) {
    return DEFAULT_BLOCK_PREVIEW_BOUNDS;
  }

  const syntheticInsert = blockInsertEntitySchema.parse({
    type: "Block",
    id: "__computeBlockBounds_preview__",
    spaceId: "model",
    blockName: spec.rootName,
    insertionPoint: { x: 0, y: 0, z: 0 },
  });
  // Use the `Block`-specific `…OrNull` resolver (NOT the generic
  // `getEntityBounds` dispatcher) so an UNRESOLVED definition is a distinct
  // `null`, never numerically indistinguishable from a LEGITIMATE degenerate
  // `{0,0,0,0}` AABB.
  const bounds = getBlockInsertBoundsOrNull(syntheticInsert, {
    blockDefinitions: spec.definitions,
  });
  if (bounds === null) {
    return DEFAULT_BLOCK_PREVIEW_BOUNDS;
  }

  return padBounds(bounds);
}

/**
 * Bounding box of a block DEFINITION's child geometry for the Insert-dialog
 * SVG preview pane's viewBox — DELIBERATELY narrower than
 * {@link computeBlockBounds}: it frames EXACTLY what the SVG preview renderer
 * (`scene/blockRender.tsx::renderBlockChildEntity`) draws.
 *
 * Computed by `computeRenderedPreviewBounds`, which walks the SAME recursion
 * the renderer walks (shared `resolveNestedBlockChild`: per-instance dynamic
 * evaluation, `composeBlockMatrix` transforms with the nested def's own base
 * point, marker insertion points, renderer-matched leaf bounds), so the
 * viewBox can never drift from the visible shape (codex PR #472 round-3
 * findings 1 + 4).
 */
export function computeBlockPreviewBounds(
  blockDef: BlockDefinition | undefined,
  blockDefinitions?: Readonly<Record<string, BlockDefinition>>,
): { minX: number; minY: number; maxX: number; maxY: number } {
  if (!blockDef) return computeBlockBounds(blockDef, blockDefinitions);
  const local = computeRenderedPreviewBounds(blockDef, blockDefinitions ?? {});
  if (!local) {
    return DEFAULT_BLOCK_PREVIEW_BOUNDS;
  }
  return padBounds(local);
}

/**
 * Build the SVG `viewBox` string for the Insert-dialog preview pane from the
 * AUTHORING-frame bounds {@link computeBlockPreviewBounds} returns.
 *
 * The preview content is drawn by the SVG renderer in RAW cad coordinates
 * (Y-up) and `InsertBlockDialog` wraps it in `<g transform="scale(1,-1)">` to
 * flip cad Y-up → SVG Y-down for display. The `viewBox` lives in the OUTER
 * svg-user frame — the frame AFTER that flip — so it must frame the
 * Y-NEGATED authoring bounds, else asymmetric content is drawn OUTSIDE the
 * frame and cropped (codex PR #472 round-5 finding 3).
 *
 * Concretely: `computeBlockPreviewBounds` frames a 0°→90° Arc at authoring
 * y∈[−11,1] (the renderer draws it via `describeArc`→`polarPoint`'s −90° phase,
 * which `previewLeafLocalBounds`'s round-4 synthetic Arc reproduces). The outer
 * `scale(1,-1)` then draws that path at svg-user y∈[−1,11], so a viewBox pinned
 * to the authoring y∈[−11,1] would crop everything above y=1. Negating Y
 * (svg-user minY = −authoring maxY; width/height unchanged, since scale(1,-1)
 * is an isometry on |Y|) puts the viewBox in the same post-flip frame the
 * content is drawn in — the Arc is then framed at y∈[−1,11] and fully visible.
 */
export function computePreviewViewBox(bounds: {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}): string {
  const width = Math.max(bounds.maxX - bounds.minX, 1);
  const height = Math.max(bounds.maxY - bounds.minY, 1);
  // svg-user (post-`scale(1,-1)`) minY = −(authoring maxY): the flip maps the
  // authoring TOP edge (maxY) to the svg-user BOTTOM (smallest Y).
  return `${bounds.minX} ${-bounds.maxY} ${width} ${height}`;
}

/** Build a preview-only EntityPreview for the SVG preview pane. The
 * synthetic entity insertion point is (0,0) with scale 1:1 so the pane
 * shows the block at its authoring origin. */
export function buildPreviewEntity(
  blockName: string,
  spaceId: string,
): EntityPreview {
  return {
    id: `__insert_preview__${blockName}`,
    label: `Insert preview ${blockName}`,
    type: "Block" as never,
    spaceId,
    summary: `Preview of block ${blockName}`,
    editableProperties: ["Layer"],
    properties: {
      Name: blockName,
      "Insertion X": "0",
      "Insertion Y": "0",
      "Scale X": "1",
      "Scale Y": "1",
      "Scale Z": "1",
      Rotation: "0",
      "Column count": "1",
      "Row count": "1",
      "Column spacing": "0",
      "Row spacing": "0",
      Color: "ByLayer",
      Linetype: "ByLayer",
      Lineweight: "ByLayer",
      Layer: "0",
    },
  };
}
